/**
 * Play a compass reply as it is synthesised.
 *
 * The engine streams 16-bit mono PCM (Piper, sentence by sentence: first
 * audio in about half a second, then 27x faster than realtime). This plays
 * each chunk as it lands instead of waiting for the file, which is the whole
 * point — a five-sentence reply starts speaking while its last sentence is
 * still being made.
 *
 * Raw PCM + Web Audio rather than <audio src>: a chunked WAV is at the mercy
 * of each browser's media buffering, MediaSource needs an encoder we do not
 * have, and neither can be stopped mid-sentence cleanly. Here every chunk is
 * scheduled on one running cursor, so playback is gapless and `stop()` is
 * immediate.
 *
 * Nothing here decides WHAT is spoken: the body carries a turn id and the
 * engine reads the reply it recorded.
 */

import { API_BASE_URL } from '../config/api'

export type SpeechFailure =
  | 'speech_not_configured'    // no key on the server — operator setup
  | 'speech_unreachable'       // sidecar down
  | 'nothing_to_say'           // the reply held no speakable text
  | 'piper_not_installed'
  | 'voice_file_missing'
  | 'unsupported'              // this browser has no Web Audio
  | string                     // no_voice_for_locale:pt_BR, upstream_502, ...

export interface SpeechHandle {
  /** Resolves when the last sentence has finished playing (or stop() ran). */
  done: Promise<void>
  /** Stop immediately; safe to call twice. */
  stop: () => void
}


// Absolute on Capacitor native builds (the app origin has no backend there),
// relative on the web. Bare fetch with a relative path would 404 on Android.
const SPEAK_URL = `${API_BASE_URL}/api/horton-v3/compass/speak`

/** One context for the tab: browsers cap how many you may create. */
let sharedCtx: AudioContext | null = null

/**
 * The live reply's loudness, for anything that has to MOVE with the voice —
 * today the face in `src/presence`, which opens a mouth with it.
 *
 * A tap on the graph that is already playing, not a second decode: the face
 * and the speaker are fed by one stream, so they cannot drift apart and the
 * PCM is never fetched or converted twice. Module-level because playback is
 * already a singleton here (one `sharedCtx`, one reply at a time); a second
 * `speakTurn` replaces the analyser exactly as it replaces the audio.
 *
 * This is DELIBERATELY an envelope and not visemes. Envelope answers "how
 * open", never "what shape" — good enough for a jaw that tracks speech, and
 * honest about it. Real mouth shapes need per-phoneme timings, which Piper
 * can produce (horton-videos' tts_piper.py already does) but lifecoach's
 * /v1/speak does not yet return. When it does, this becomes the fallback for
 * turns that lack a schedule rather than the only path.
 */
let analyser: AnalyserNode | null = null
let envelopeBuf: Float32Array<ArrayBuffer> | null = null

/**
 * 0..1 loudness of what is playing right now, 0 when nothing is.
 *
 * RMS, not peak: peak follows the waveform's fine structure and jitters at
 * pitch-period rate, which on a jaw reads as a chatter rather than speech.
 */
export function speechEnvelope(): number {
  const a = analyser
  if (!a) return 0
  if (!envelopeBuf || envelopeBuf.length !== a.fftSize) {
    envelopeBuf = new Float32Array(a.fftSize)
  }
  try {
    a.getFloatTimeDomainData(envelopeBuf)
  } catch {
    return 0                       // context closed under us
  }
  let sum = 0
  for (let i = 0; i < envelopeBuf.length; i++) {
    const v = envelopeBuf[i]!
    sum += v * v
  }
  const rms = Math.sqrt(sum / envelopeBuf.length)
  // Speech RMS sits well below 1. Scaling here rather than in the face keeps
  // "how loud is it" in one place and leaves the face to decide what loudness
  // MEANS for a mouth.
  return Math.min(1, rms * 4)
}

/**
 * A context AT THE STREAM'S SAMPLE RATE. The default is the hardware rate
 * (usually 48000) and every 22050 Hz buffer would then be resampled
 * independently — a small artefact at every sentence seam. Asking for the
 * rate up front makes each buffer play as it was made. Browsers that refuse
 * the option fall back to the default context, which still works.
 */
function context(rate?: number): AudioContext | null {
  const Ctor: typeof AudioContext | undefined =
    (window as any).AudioContext || (window as any).webkitAudioContext
  if (!Ctor) return null
  if (sharedCtx && sharedCtx.state !== 'closed'
      && (!rate || sharedCtx.sampleRate === rate)) return sharedCtx
  if (sharedCtx && sharedCtx.state !== 'closed' && rate) {
    // A context locked to the wrong rate: close it rather than resample
    // every buffer for the rest of the session.
    try { void sharedCtx.close() } catch { /* already going */ }
  }
  try {
    sharedCtx = rate ? new Ctor({ sampleRate: rate }) : new Ctor()
  } catch {
    sharedCtx = new Ctor()
  }
  return sharedCtx
}

/**
 * True when speech can be played at all. A browser without Web Audio (or a
 * page that has never had a user gesture) gets the caller's fallback rather
 * than silence.
 */
export function speechSupported(): boolean {
  return Boolean((window as any).AudioContext || (window as any).webkitAudioContext)
}

/**
 * Speak a recorded turn. Rejects with a SpeechFailure string the caller can
 * show or fall back on — never a silent failure.
 */
/**
 * The sidecar's rate today. The context has to exist INSIDE the click that
 * asked for audio (autoplay policy), which is before any header can tell us
 * the real rate; a context at the wrong rate would be closed and rebuilt
 * outside the gesture, which is exactly the failure the guard below exists
 * to catch. The stream's own X-Sample-Rate is still honoured per buffer.
 */
const EXPECTED_RATE = 22050

export function speakTurn(
  turnId: number,
  opts: { volume?: number; auto?: boolean } = {},
): SpeechHandle {
  // Synchronously, in the gesture: a context built after `await fetch` is
  // born outside it and on iOS/WKWebView stays suspended forever.
  const ctx = context(EXPECTED_RATE)
  let stopped = false
  const sources: AudioBufferSourceNode[] = []
  const controller = new AbortController()

  let fade: GainNode | null = null
  // Only THIS call's tap may be torn down: a newer reply that has already
  // replaced it must keep playing (and moving the face).
  let tapRef: AnalyserNode | null = null

  const stop = () => {
    if (stopped) return
    stopped = true
    controller.abort()
    // Cutting a source mid-waveform is an audible click at full scale, and
    // barge-in ("stop, I've heard enough") is a thing people do constantly.
    // A 15ms ramp is inaudible as a fade and removes the click.
    const ctx = sharedCtx
    if (fade && ctx) {
      try {
        const t = ctx.currentTime
        fade.gain.cancelScheduledValues(t)
        fade.gain.setValueAtTime(fade.gain.value, t)
        fade.gain.linearRampToValueAtTime(0, t + 0.015)
      } catch { /* a closed context: fall through to the hard stop */ }
    }
    // Silence reads as silence immediately. Left connected, the analyser
    // would keep reporting the last buffered samples and the mouth would go
    // on moving after the voice had been cut.
    if (analyser === tapRef) analyser = null
    const cut = sources.slice()
    sources.length = 0
    setTimeout(() => {
      for (const s of cut) {
        try { s.stop() } catch { /* already ended */ }
      }
    }, 20)
  }

  const done = (async () => {
    if (!ctx) throw 'unsupported' as SpeechFailure
    // Resume while the gesture is still on the stack. A suspended context
    // plays nothing while every call reports success — silence the caller
    // cannot tell from working, which is what law 4 forbids.
    if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined)
    if (ctx.state !== 'running') throw 'audio_blocked_until_you_tap' as SpeechFailure

    const res = await fetch(SPEAK_URL, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      // `auto` says the APP started this, not the person. The engine
      // refuses to auto-speak a reply built from material the person marked
      // as never-quoted-back; pressing listen sends no flag and speaks it.
      body: JSON.stringify({
        turn_id: turnId,
        auto: opts.auto === true,
        // The device's language. The engine uses it only when nothing is
        // recorded for this person, and it is what lets the engine REFUSE
        // rather than read someone's Spanish with an English voice.
        locale: typeof navigator !== 'undefined' ? navigator.language : undefined,
      }),
      signal: controller.signal,
    })
    if (!res.ok || !res.body) {
      let reason: SpeechFailure = `upstream_${res.status}`
      try {
        const j = await res.json()
        if (j && typeof j.reason === 'string') reason = j.reason
      } catch { /* not json: keep the status */ }
      throw reason
    }

    const rate = Number(res.headers.get('X-Sample-Rate') || EXPECTED_RATE)
    const gain = ctx.createGain()
    gain.gain.value = opts.volume ?? 1
    // gain -> analyser -> destination. In line rather than a side branch so
    // the analyser sees exactly what the speaker does, including the
    // barge-in fade below: when a reply is cut off the face stops with it.
    const tap = ctx.createAnalyser()
    tap.fftSize = 1024
    gain.connect(tap)
    tap.connect(ctx.destination)
    analyser = tap
    tapRef = tap
    fade = gain

    const reader = res.body.getReader()
    // One AudioBufferSourceNode per TCP segment would be hundreds of nodes
    // for a long reply. Accumulate to about a fifth of a second before
    // scheduling: still far below the time-to-first-audio, and a small
    // fraction of the node count.
    const MIN_SCHEDULE_BYTES = Math.max(2, Math.floor(rate * 2 * 0.2))
    let pending: Uint8Array[] = []
    let pendingBytes = 0
    // The cursor every chunk is scheduled against. Kept a beat ahead of
    // ctx.currentTime so the first buffer is not scheduled in the past; after
    // that it advances by exactly each chunk's duration, which is what makes
    // consecutive sentences gapless rather than merely fast.
    let cursor = 0
    let carry = new Uint8Array(0)     // a chunk can split an int16 in half
    const endings: Promise<void>[] = []

    const joinChunks = (parts: Uint8Array[], total: number): Uint8Array => {
      if (parts.length === 1) return parts[0]!
      const out = new Uint8Array(total)
      let at = 0
      for (const p of parts) { out.set(p, at); at += p.byteLength }
      return out
    }

    const schedule = (bytes: Uint8Array) => {
      const samples = bytes.byteLength >> 1
      if (!samples) return
      const view = new DataView(bytes.buffer, bytes.byteOffset, samples * 2)
      const buf = ctx.createBuffer(1, samples, rate)
      const channel = buf.getChannelData(0)
      for (let i = 0; i < samples; i++) {
        channel[i] = view.getInt16(i * 2, true) / 32768   // s16le -> float
      }
      const src = ctx.createBufferSource()
      src.buffer = buf
      src.connect(gain)
      const startAt = Math.max(cursor, ctx.currentTime + 0.06)
      src.start(startAt)
      cursor = startAt + buf.duration
      sources.push(src)
      endings.push(new Promise<void>((resolve) => { src.onended = () => resolve() }))
    }

    // A stream that stops producing must not leave the caller pending
    // forever — the control would keep saying "stop" over silence with no
    // reason. The deadline is the audio already buffered plus a margin, so
    // a slow-but-alive stream is never cut off.
    const readWithDeadline = async () => {
      const buffered = Math.max(0, cursor - ctx.currentTime)
      const ms = Math.round((buffered + 20) * 1000)
      let timer: ReturnType<typeof setTimeout> | undefined
      try {
        return await Promise.race([
          reader.read(),
          new Promise<never>((_, reject) => {
            timer = setTimeout(() => reject('speech_stalled' as SpeechFailure), ms)
          }),
        ])
      } finally {
        if (timer) clearTimeout(timer)
      }
    }

    try {
      for (;;) {
        const { done: finished, value } = await readWithDeadline()
        if (finished) break
        if (stopped || !value) break
        let bytes = value
        if (carry.byteLength) {
          const joined = new Uint8Array(carry.byteLength + value.byteLength)
          joined.set(carry, 0); joined.set(value, carry.byteLength)
          bytes = joined
          carry = new Uint8Array(0)
        }
        const usable = bytes.byteLength - (bytes.byteLength % 2)
        if (usable < bytes.byteLength) carry = bytes.slice(usable)
        // Copy: the scheduled buffer must not alias a stream buffer the
        // reader may reuse.
        pending.push(new Uint8Array(bytes.slice(0, usable)))
        pendingBytes += usable
        if (pendingBytes >= MIN_SCHEDULE_BYTES) {
          schedule(joinChunks(pending, pendingBytes))
          pending = []; pendingBytes = 0
        }
      }
      if (pendingBytes && !stopped) schedule(joinChunks(pending, pendingBytes))
    } finally {
      // cancel() on an ERRORED stream returns a REJECTED promise (the abort
      // in stop() errors it), so the sync try/catch alone left an unhandled
      // rejection on every stop.
      try { void reader.cancel().catch(() => undefined) } catch { /* released */ }
    }
    if (!stopped) await Promise.all(endings)
  })()

  return { done, stop }
}
